import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../../WebApp';

const MEDIA_BASE =
  process.env.NODE_ENV === 'production'
    ? 'https://hotbump.online'
    : 'http://localhost:8010';

const resolveMediaUrl = (url) => {
  if (!url) return '/avatars/default.png';
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  return `${MEDIA_BASE}${url}`;
};

const safeParseJSON = (value, fallback) => {
  try {
    return value ? JSON.parse(value) : fallback;
  } catch {
    return fallback;
  }
};

const getRecentChatsKey = (user) => `hotbump_recent_chats_${user?.id || user?.email || 'guest'}`;

const parseStatNumber = (value) => {
  if (value === null || value === undefined) return 0;
  if (typeof value === 'number') return value;
  const numeric = Number(String(value).replace(/[^0-9.]/g, ''));
  return Number.isNaN(numeric) ? 0 : numeric;
};

const formatStat = (value) => {
  const numeric = parseStatNumber(value);
  if (numeric >= 1000) {
    return `${(numeric / 1000).toFixed(1)}k`;
  }
  return numeric;
};

function MyChats() {
  const { user, language } = React.useContext(AuthContext);
  const [recentIds, setRecentIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [characters, setCharacters] = useState([]);
  const [groupChats, setGroupChats] = useState([]);
  const [groupLoading, setGroupLoading] = useState(true);
  const [groupError, setGroupError] = useState('');
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [models, setModels] = useState([]);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [selectedModelIds, setSelectedModelIds] = useState([]);
  const [invitedNickname, setInvitedNickname] = useState('');
  const [order, setOrder] = useState([]);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  const navigate = useNavigate();
  const userKey = user ? `user:${user.id}` : 'user:self';

  useEffect(() => {
    if (!user) return;
    const stored = safeParseJSON(localStorage.getItem(getRecentChatsKey(user)), []);
    setRecentIds(stored);
  }, [user]);

  useEffect(() => {
    let cancelled = false;
    const loadSummaries = async () => {
      if (!user) {
        setLoading(false);
        setCharacters([]);
        return;
      }
      if (!recentIds.length) {
        setCharacters([]);
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const response = await axios.get('/web/characters/summaries', {
          params: { ids: recentIds.join(','), language }
        });
        if (!cancelled) {
          const summaries = response.data.characters || [];
          const ordered = recentIds
            .map((id) => summaries.find((item) => item.id === id))
            .filter(Boolean);
          setCharacters(ordered);
        }
      } catch (error) {
        console.error('Failed to load recent chats:', error);
        if (!cancelled) {
          setCharacters([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    loadSummaries();
    return () => {
      cancelled = true;
    };
  }, [recentIds, user, language]);

  useEffect(() => {
    if (!user) {
      setGroupChats([]);
      setGroupLoading(false);
      return;
    }
    const loadGroups = async () => {
      setGroupLoading(true);
      setGroupError('');
      try {
        const response = await axios.get('/web/group-chats');
        setGroupChats(response.data?.chats || []);
      } catch (error) {
        const detail =
          error?.response?.data?.detail ||
          (language === 'en' ? 'Failed to load group chats' : 'Не удалось загрузить групповые чаты');
        setGroupError(detail);
      } finally {
        setGroupLoading(false);
      }
    };
    loadGroups();
  }, [user, language]);

  useEffect(() => {
    if (!createModalOpen) return;
    if (models.length > 0) return;
    const loadModels = async () => {
      setModelsLoading(true);
      try {
        const response = await axios.get('/explore/characters', { params: { limit: 80, language } });
        setModels(response.data?.characters || []);
      } catch (error) {
        console.error('Failed to load models', error);
      } finally {
        setModelsLoading(false);
      }
    };
    loadModels();
  }, [createModalOpen, models.length, language]);

  useEffect(() => {
    const baseOrder = [userKey, ...selectedModelIds.map((id) => `character:${id}`)];
    setOrder((prev) => {
      const filtered = prev.filter((key) => baseOrder.includes(key));
      const missing = baseOrder.filter((key) => !filtered.includes(key));
      return [...filtered, ...missing];
    });
  }, [selectedModelIds, userKey]);

  const toggleModel = (id) => {
    setSelectedModelIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const moveOrder = (key, direction) => {
    setOrder((prev) => {
      const idx = prev.indexOf(key);
      if (idx === -1) return prev;
      if (key === userKey && direction < 0) return prev; // creator stays first
      const newIdx = Math.min(prev.length - 1, Math.max(0, idx + direction));
      if (newIdx === idx) return prev;
      const updated = [...prev];
      const [item] = updated.splice(idx, 1);
      updated.splice(newIdx, 0, item);
      return updated;
    });
  };

  const handleCreateGroup = async () => {
    if (!selectedModelIds.length) {
      setCreateError(language === 'en' ? 'Select at least one model' : 'Выберите хотя бы одну модель');
      return;
    }
    setCreateError('');
    setCreating(true);
    try {
      const response = await axios.post('/web/group-chats', {
        character_ids: selectedModelIds,
        invited_nickname: invitedNickname.trim() || undefined,
        order,
      });
      if (response?.data?.id) {
        setCreateModalOpen(false);
        navigate(`/web/group-chat/${response.data.id}`);
      }
    } catch (error) {
      const detail =
        error?.response?.data?.detail ||
        (language === 'en' ? 'Failed to create chat' : 'Не удалось создать чат');
      setCreateError(detail);
    } finally {
      setCreating(false);
    }
  };

  if (!user) {
    return (
      <div className="text-center text-white py-16">
        {language === 'en'
          ? 'Login to see your chats'
          : 'Авторизуйтесь, чтобы увидеть ваши чаты'}
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="text-center">
        <h1 className="text-3xl font-semibold text-white">
          {language === 'en' ? 'My Chats' : 'Мои чаты'}
        </h1>
        <p className="text-gray-400 mt-2">
          {language === 'en'
            ? 'Return to any model you have already opened.'
            : 'Возвращайтесь к моделям, с которыми уже переписывались.'}
        </p>
        <button
          type="button"
          onClick={() => { setCreateModalOpen(true); setCreateError(''); }}
          className="mt-3 inline-flex items-center gap-2 text-burgundy-200 hover:text-white font-semibold"
        >
          <span className="text-lg">＋</span>
          <span>
            {language === 'en'
              ? 'Create a chat with one or several models'
              : 'создай чат с одной или несколькими моделями'}
          </span>
        </button>
      </div>

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold">{language === 'en' ? 'Group chats' : 'Групповые чаты'}</h2>
        </div>
        {groupLoading ? (
          <div className="text-center text-gray-400 py-6">
            {language === 'en' ? 'Loading group chats...' : 'Загрузка групповых чатов...'}
          </div>
        ) : groupError ? (
          <div className="text-center text-red-300 bg-red-900/30 border border-red-600/30 rounded-xl p-4">
            {groupError}
          </div>
        ) : groupChats.length === 0 ? (
          <div className="text-center text-gray-400 py-6">
            {language === 'en' ? 'No group chats yet.' : 'Групповых чатов пока нет.'}
          </div>
        ) : (
          <div className="space-y-4">
            {groupChats.map((chat) => (
              <div
                key={chat.id}
                className="rounded-3xl border border-white/10 bg-black/40 p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
              >
                <div className="space-y-2">
                  <div className="text-lg font-semibold text-white">
                    {chat.title || (language === 'en' ? 'Group chat' : 'Групповой чат')}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {chat.participants?.map((p) => (
                      <span
                        key={p.key}
                        className={`text-xs px-3 py-1 rounded-full border ${
                          p.is_current_turn ? 'border-burgundy-500 text-burgundy-200' : 'border-white/10 text-gray-200'
                        }`}
                      >
                        {p.name}
                      </span>
                    ))}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => navigate(`/web/group-chat/${chat.id}`)}
                  className="px-4 py-2 rounded-xl bg-burgundy-600 hover:bg-burgundy-500 text-white font-semibold"
                >
                  {language === 'en' ? 'Open group chat' : 'Открыть групповой чат'}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="space-y-4">
        <h2 className="text-xl font-semibold">{language === 'en' ? 'Direct chats' : 'Обычные чаты'}</h2>
        {loading ? (
          <div className="text-center text-gray-400 py-16">
            {language === 'en' ? 'Loading chats...' : 'Загрузка чатов...'}
          </div>
        ) : characters.length === 0 ? (
          <div className="text-center text-gray-400 py-16">
            {language === 'en'
              ? 'You have not opened any chats yet.'
              : 'Вы ещё не начали ни один чат.'}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6">
            {characters.map((character) => (
              <div
                key={character.id}
                className="rounded-3xl border border-white/10 bg-black/40 backdrop-blur p-5 md:p-6 flex gap-6 items-start"
              >
                <img
                  src={resolveMediaUrl(character.avatar_url || character.avatar)}
                  alt={character.name}
                  className="h-48 w-48 rounded-2xl object-contain border border-white/10 bg-black/40 p-2 flex-shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-3">
                    <h2 className="text-xl font-semibold truncate">{character.name}</h2>
                    <span className="text-sm text-gray-400">
                      ❤️ {formatStat(character.likes)}
                    </span>
                  </div>
                  {character.about && (
                    <p className="text-sm text-gray-300 mt-2 line-clamp-3">
                      {character.about}
                    </p>
                  )}
                  <button
                    type="button"
                    onClick={() => navigate(`/web/chat/${character.id}`)}
                    className="mt-4 inline-flex items-center justify-center px-4 py-2 rounded-xl bg-burgundy-600 hover:bg-burgundy-500 text-white text-sm font-semibold transition"
                  >
                    {language === 'en' ? 'Open chat' : 'Открыть чат'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {createModalOpen && (
        <div className="fixed inset-0 z-[9000] bg-black/70 backdrop-blur-sm flex items-center justify-center px-4">
          <div className="bg-dark-800 border border-burgundy-800 rounded-3xl max-w-4xl w-full p-6 space-y-4 overflow-y-auto max-h-[90vh]">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="text-2xl font-semibold text-white">
                  {language === 'en' ? 'Create group chat' : 'Создать групповой чат'}
                </h3>
                <p className="text-sm text-gray-400">
                  {language === 'en'
                    ? 'Pick models and (optionally) invite another user by full nickname.'
                    : 'Выберите моделей и при желании пригласите другого пользователя по точному нику.'}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setCreateModalOpen(false)}
                className="text-gray-400 hover:text-white text-2xl"
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className="space-y-3">
              <label className="block text-sm text-gray-300">
                {language === 'en' ? 'Invite user by nickname (optional)' : 'Пригласить пользователя по нику (опционально)'}
              </label>
              <input
                value={invitedNickname}
                onChange={(e) => setInvitedNickname(e.target.value)}
                className="w-full px-4 py-3 rounded-lg bg-dark-700 border border-white/10 text-white focus:outline-none focus:ring-2 focus:ring-burgundy-500"
                placeholder={language === 'en' ? 'Exact nickname' : 'Полный никнейм'}
              />
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="text-lg font-semibold">{language === 'en' ? 'Choose models' : 'Выберите моделей'}</h4>
                {modelsLoading && (
                  <span className="text-xs text-gray-400">
                    {language === 'en' ? 'Loading...' : 'Загрузка...'}
                  </span>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 max-h-72 overflow-y-auto">
                {models.map((model) => {
                  const checked = selectedModelIds.includes(model.id);
                  return (
                    <button
                      key={model.id}
                      type="button"
                      onClick={() => toggleModel(model.id)}
                      className={`flex items-center gap-3 p-3 rounded-2xl border transition ${
                        checked ? 'border-burgundy-500 bg-burgundy-900/30' : 'border-white/10 bg-black/30'
                      }`}
                    >
                      <img
                        src={resolveMediaUrl(model.avatar_url || model.avatar)}
                        alt={model.name}
                        className="h-16 w-16 rounded-xl object-cover"
                      />
                      <div className="flex-1 text-left">
                        <div className="font-semibold text-white">{model.name}</div>
                        <div className="text-xs text-gray-400 line-clamp-2">
                          {language === 'en'
                            ? (model.description_en || model.description || '')
                            : (model.description || model.description_en || '')}
                        </div>
                      </div>
                      <input type="checkbox" readOnly checked={checked} className="h-5 w-5" />
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="space-y-2">
              <h4 className="text-lg font-semibold">{language === 'en' ? 'Order (carousel)' : 'Порядок (карусель)'}</h4>
              <p className="text-xs text-gray-400">
                {language === 'en'
                  ? 'Set who writes in which sequence. Creator stays first.'
                  : 'Установите последовательность сообщений. Создатель остаётся первым.'}
              </p>
              <div className="space-y-2">
                {order.map((key) => {
                  const isCreator = key === userKey;
                  const modelId = key.startsWith('character:') ? key.split(':')[1] : null;
                  const model = models.find((m) => m.id === modelId);
                  const label = isCreator
                    ? (language === 'en' ? 'You' : 'Вы')
                    : model?.name || key;
                  return (
                    <div
                      key={key}
                      className="flex items-center justify-between rounded-xl border border-white/10 bg-black/30 px-3 py-2"
                    >
                      <span className="text-white text-sm">{label}</span>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => moveOrder(key, -1)}
                          disabled={isCreator}
                          className="px-2 py-1 rounded-lg bg-dark-700 border border-white/10 text-gray-200 disabled:opacity-50"
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          onClick={() => moveOrder(key, 1)}
                          disabled={isCreator}
                          className="px-2 py-1 rounded-lg bg-dark-700 border border-white/10 text-gray-200 disabled:opacity-50"
                        >
                          ↓
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {createError && (
              <div className="text-sm text-red-300 bg-red-900/30 border border-red-600/30 px-3 py-2 rounded-xl">
                {createError}
              </div>
            )}

            <div className="flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setCreateModalOpen(false)}
                className="px-4 py-2 rounded-xl border border-white/20 text-gray-200 hover:bg-white/5"
              >
                {language === 'en' ? 'Cancel' : 'Отмена'}
              </button>
              <button
                type="button"
                onClick={handleCreateGroup}
                disabled={creating || selectedModelIds.length === 0}
                className="px-5 py-2 rounded-xl bg-burgundy-600 hover:bg-burgundy-500 text-white font-semibold disabled:opacity-50"
              >
                {creating ? (language === 'en' ? 'Creating...' : 'Создание...') : (language === 'en' ? 'Create chat' : 'Создать чат')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MyChats;

